/**
 * usePendingInvitees — list invitees the caller has invited who haven't
 * Acknowledged yet.
 *
 * Phase G.7.11 (sacred/change-doctrine.md §VIII Pending Invitations):
 *   The inviter sees their open invitations — who they've recognized,
 *   when, anonymously or not. They may resend the magic link (re-trigger
 *   signInWithOtp) or cancel the invitation (delete Contact + auth.user).
 *
 * Backed by RPC `core.list_my_pending_invitees()` which is SECURITY DEFINER
 * and returns only rows where invitationSentBy = caller's contact id AND
 * isOrchist = false.
 */

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export interface PendingInviteeRow {
  id: string;
  email: string;
  displayName: string;
  invitationSentAt: string | null;
  invitationProposedAcceptedAt: string | null;
  invitationAnonymous: boolean;
  createdAt: string;
  hasAuthUser: boolean;
}

export function usePendingInvitees() {
  const { contact } = useAuth();
  const [rows, setRows] = useState<PendingInviteeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!contact) {
      setRows([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const { data, error: err } = await supabase
        .schema('core')
        .rpc('list_my_pending_invitees');
      if (cancelled) return;
      if (err) {
        setError(new Error(err.message));
        setRows([]);
        setLoading(false);
        return;
      }
      setRows((data ?? []) as PendingInviteeRow[]);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [contact, refreshKey]);

  const cancelInvitation = useCallback(
    async (inviteeId: string) => {
      const { error: err } = await supabase
        .schema('core')
        .rpc('cancel_invitation', { p_invitee_contact_id: inviteeId });
      if (err) throw new Error(err.message);
      refresh();
    },
    [refresh]
  );

  const resendInvitation = useCallback(async (email: string) => {
    const { error: err } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: `${window.location.origin}/auth/callback` },
    });
    if (err) throw new Error(err.message);
  }, []);

  return { rows, loading, error, refresh, cancelInvitation, resendInvitation };
}
